import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarX, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SOURCE = { worker_link: "Worker · reminder link", worker_app: "Worker · app", admin: "Office" };

// Dashboard card: who reported "no work" for the last completed week.
export default function NoWorkCard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/dashboard/no-work").then((r) => setData(r.data)).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  if (!data || data.rows.length === 0) return null;

  const undo = async (row) => {
    if (busy) return;
    if (!window.confirm(`Clear "No work" for ${row.worker_name} on ${row.project_name}?\n\nTimesheet reminders for the week of ${data.week_pretty} will resume.`)) return;
    setBusy(row.assignment_id);
    try {
      await api.post(`/assignments/${row.assignment_id}/no-work`, { on: false, week_start: data.week_start });
      toast.success(`Cleared — ${row.worker_name} is back to a normal week.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setBusy(""); }
  };

  const idle = data.idle_projects;
  return (
    <div data-testid="no-work-card" className="mt-4 border-2 border-blue-300 bg-blue-50 rounded-lg p-5 shadow-sm">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-md bg-blue-600 text-white"><CalendarX size={22} weight="fill" /></div>
          <div>
            <div className="tinylabel text-blue-900">No work reported · week of {data.week_pretty}</div>
            <div className="text-xl font-black text-slate-900 mt-0.5" data-testid="no-work-card-headline">
              {data.rows.length} worker{data.rows.length === 1 ? "" : "s"} had no hours last week
            </div>
            <div className="text-sm text-slate-700 mt-1">
              {idle.length > 0 ? (
                <span data-testid="no-work-idle-summary">
                  <b className="text-rose-700">{idle.length} project{idle.length === 1 ? "" : "s"} fully idle</b> — every assigned worker reported no work:{" "}
                  {idle.map((p, i) => (
                    <span key={p.project_id}>{i > 0 && ", "}<Link to="/projects" className="font-semibold text-rose-700 hover:underline" data-testid={`no-work-idle-project-${p.project_id}`}>{p.project_name}</Link>{p.client_name ? ` (${p.client_name})` : ""}</span>
                  ))}.
                </span>
              ) : "No project is fully idle — other workers on these projects are still active."}
              {" "}Reminders are paused for these workers this week.
            </div>
          </div>
        </div>
        <Link to="/assignments" className="text-xs font-semibold text-blue-800 hover:underline" data-testid="no-work-card-link">Open Assignments →</Link>
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-blue-200">
              <th className="py-2 pr-4">Worker</th>
              <th className="py-2 pr-4">Project</th>
              <th className="py-2 pr-4">Client</th>
              <th className="py-2 pr-4">Reported by</th>
              <th className="py-2 pr-4">When</th>
              {isAdmin && <th className="py-2 pr-4 text-right">Action</th>}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.assignment_id} data-testid={`no-work-row-${r.assignment_id}`} className="border-b border-blue-100 last:border-none">
                <td className="py-2 pr-4 font-semibold">
                  {isAdmin ? <Link to={`/workers/${r.worker_id}`} className="text-slate-900 hover:text-blue-700">{r.worker_name}</Link> : r.worker_name}
                </td>
                <td className="py-2 pr-4">
                  {r.project_name}
                  {r.project_idle && <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-rose-100 text-rose-700" data-testid={`no-work-idle-tag-${r.assignment_id}`}>Project idle</span>}
                </td>
                <td className="py-2 pr-4 text-slate-600">{r.client_name || "—"}</td>
                <td className="py-2 pr-4 text-slate-600" data-testid={`no-work-source-${r.assignment_id}`}>{SOURCE[r.reported_by] || "—"}</td>
                <td className="py-2 pr-4 text-slate-500 text-xs">{r.reported_at ? new Date(r.reported_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}</td>
                {isAdmin && (
                  <td className="py-2 pr-4 text-right">
                    <button type="button" onClick={() => undo(r)} disabled={busy === r.assignment_id} data-testid={`no-work-undo-${r.assignment_id}`}
                      title="Clear the flag — reminders resume for this worker."
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded border border-blue-400 bg-white text-blue-800 hover:bg-blue-100 disabled:opacity-50">
                      <ArrowCounterClockwise size={13} weight="bold" />{busy === r.assignment_id ? "…" : "Undo"}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
